import { Dispatch } from "react";

import store from "state/store";

import {
  OPEN_COMPOSER,
  CLOSE_COMPOSER,
  TOGGLE_PRO_MODE,
  SET_IN_DRAFT_MODE,
  SET_POST_DATA,
  RESET_POST_DATA,
  ENABLE_SPLIT_MODE,
  DISABLE_SPLIT_MODE,
  SET_TAGS,
  SET_EDITOR_STATE_OBJ,
  SET_VISIBLE_CAPTION,
  SET_VALIDATION_ERRORS,
  SET_CHOSEN_SUGGESTED_CAPTION,
  SET_POST_CONCEPTS,
  SET_POST_IDEA_CAPTIONS,
  SET_ATTACHMENT_TYPE,
  SET_IS_UPLOADING_ATTACHMENTS,
  ADD_ATTACHMENTS,
  SET_SCRAPE_RESULT,
  REMOVE_ATTACHMENT,
  REMOVE_ARTICLE_ATTACHMENT,
  REORDER_ATTACHMENT,
  SET_ATTACHMENT_VALIDATION_ERROR,
  SET_ATTACHMENTS,
  SET_ATTACHMENT_OPTIONS,
  SET_ATTACHMENT_HTML,
  SET_IS_SUBMITTING,
  SET_IS_DELETING,
  SET_WAS_SCHEDULE_CHANGED,
  SET_PICKED_DATE,
  SET_MOOD_FILTER,
  SET_SCHEDULER_OPTION_ID,
  SET_AGENT_CHAT,
  MARK_AGENT_CHANGE,
  SET_VISUAL_OVERFLOW,
  SET_IS_LOADING_SUGGESTED_IMAGES,
  SET_NEW_IMAGES_INDICATION,
  SET_IMAGES_SEARCH_TERM,
  SET_SUGGESTED_IMAGES,
  SET_IMAGES_QUERY,
  SET_ACTIVE_TOOLKIT,
  SET_CONFIRMATION_MODAL_PROPS,
  SET_IS_COMPOSER_CLOSE_CONFIRMATION_OPEN,
  SET_CAPTION,
  ADD_COMPOSER_NOTIFICATION,
  REMOVE_COMPOSER_NOTIFICATION
} from "./actions";
import { IActions } from "./actionCreators";
import * as storageService from "utils/storage";
import createEvent, { ICustomEvent } from "utils/events";
import { KEY_COMPOSER_PRO_MODE } from "./useComposerContext";
import { trackAnalyticsEvent } from "state/actions/AnalyticsActions";
import { AgentMessage, ProfileChooser } from "components/composerv2/components/toolkit/CreateAI/protocol";
import type { ComposerAgentProfile } from "services/composerAgent";
import { SessionLogEntry } from "components/composerv2/components/toolkit/CreateAI/sessionLog";

export interface IMoodFilter {
  label: string;
  value: string;
}

export const MOOD_FILTER_OPTIONS: IMoodFilter[] = [
  {
    label: "Neutral",
    value: "neutral"
  },
  {
    label: "Happy",
    value: "happy"
  },
  {
    label: "Sad",
    value: "sad"
  },
  {
    label: "Funny",
    value: "funny"
  },
  {
    label: "Clever",
    value: "clever"
  },
  {
    label: "Inspiring",
    value: "inspiring"
  },
  {
    label: "Motivational",
    value: "motivational"
  },
  {
    label: "Thoughtful",
    value: "thoughtful"
  },
  {
    label: "Proud",
    value: "proud"
  }
];

// TODO: Turn these into a proper defined interface
export interface IPostData {
  [key: string]: any;
}

export interface ITag {
  [key: string]: any;
}

export interface IValidationError {
  [key: string]: {
    [key: string]: any;
  };
}

export interface ICaption {
  id: string;
  title: string;
  caption: string;
}

export interface IPostIdea {
  id: string;
  title: string;
  subtitle: string;
  captions: ICaption[];
}

export interface IPostConcept {
  id: string;
  captions: any[];
  primaryColor: string;
  secondaryColor: string;
  shortDescription: string;
  slug: string;
  title: string;
  postIdeas: IPostIdea[];
}

export type AttachmentType = "photo" | "video" | "pdf" | "article" | "html";

export interface IUploadedAttachment {
  url: string;
  metaData: { [key: string]: any };
}

export interface IScrappedAttachment {
  [key: string]: any;
}

export interface IUploadedAttachments {
  isUploading: boolean;
  attachments: IUploadedAttachment[];
}

export interface IScrappedAttachments {
  isScrapping: boolean;
  attachment: IScrappedAttachment | null;
}

export interface IChannelAttachments {
  validationErrors: any[];
  type: AttachmentType | null;
  options: { [key: string]: any };
  photoAttachments: IUploadedAttachments;
  videoAttachments: IUploadedAttachments;
  pdfAttachments: IUploadedAttachments;
  articleAttachments: IScrappedAttachments;
  html?: string;
  htmlMetaData?: { [key: string]: any };
  htmlClippedSlides?: number[]; // slides whose text overflows the canvas, as the visual editor measured them
  htmlEdited?: boolean; // the html changed since the post was loaded, so the saved preview no longer shows it
}

export interface IAttachments {
  [key: string]: IChannelAttachments;
}

export type SchedulerOptionId =
  | "set_date_time"
  | "auto_schedule"
  | "best_time_today"
  | "best_time_tomorrow"
  | "best_time_day_after_tomorrow"
  | "post_now";

export interface IScheduler {
  isSubmitting: boolean;
  isDeleting: boolean;
  wasScheduleChanged: boolean;
  pickedDate: null | Date;
  optionId: null | SchedulerOptionId; // null until something picks one; the scheduler derives a default
}

export type ToolkitTools = "captionSuggestion" | "imageSuggestion" | "createAI";

export interface IAgentChat {
  history: AgentMessage[]; // the Create AI conversation for the open post, kept across toolkit tab switches
  previousLog: SessionLogEntry[] | null; // what earlier chats on this post did; null until loaded
  profileId: string | null; // the Willow Create profile the chat writes with; null until the picker has loaded
  profileChosenBy: ProfileChooser | null; // null while profileId is the default, the newest profile
  profiles: ComposerAgentProfile[]; // the account's ready Create profiles, newest first; empty until loaded
  isReplying: boolean; // a reply keeps running when the chat unmounts (tab switch, pro mode), so a remounted chat must not start a second one
  chatId: string | null; // ties the chat's steps and events together; null until the chat first shows
}

export const AGENT_CHANGE_AREAS = ["caption", "visual", "attachments", "channels", "schedule", "category", "profile"] as const;
export type AgentChangeArea = (typeof AGENT_CHANGE_AREAS)[number];
export type IAgentTouch = Record<AgentChangeArea, number | null>; // when Create AI last changed each part of the post, as millis since epoch

export interface IConfirmationModalProps {
  title: string;
  type?: string;
  description: string;
  showOptions: boolean;
  buttonProps: {
    variant: string;
    label: string;
    action: Function;
  };
}

export interface IComposerNotification {
  id: string;
  type: "warning" | "error" | "info";
  service?: string;
  message: string | JSX.Element;
}


export interface IComposerState {
  isComposerOpen: boolean;
  inProMode: boolean;
  inDraftMode: boolean;
  postData: IPostData;
  inSplitMode: boolean;
  tags: ITag[];
  editorStateObj: any;
  visibleCaption: string;
  validationErrors: IValidationError[];
  chosenSuggestedCaption: { [key: string]: string };
  postConcepts: null | IPostConcept[];
  postIdea: IPostIdea | null;
  attachments: IAttachments;
  scrapeResultsByUrl: { [url: string]: IScrappedAttachment };
  scheduler: IScheduler;
  moodFilter: IMoodFilter;
  suggestedImages: {
    isLoading: boolean;
    newImagesIndication: boolean;
    searchTerm: string;
    images: any[];
    query: string;
  };
  activeToolkit: ToolkitTools;
  agentChat: IAgentChat;
  agentTouch: IAgentTouch;
  events: {
    onPostedEvent: ICustomEvent;
    onReloadEvent: ICustomEvent;
    onDeletedEvent: ICustomEvent;
  };
  confirmationModalProps: null | IConfirmationModalProps;
  isComposerCloseConfirmationOpen: boolean;
  closeToRefreshChannels: boolean;
  // Note Updating this does not change composer text. it was created for other components that need access to composer text.
  // Do not call setCaptionAction expecting it to change
  caption: string;
  composerNotifications: IComposerNotification[];
}

export type IComposerDispatch = Dispatch<IActions>;

const uploadedAttachmentsInitialValue: IUploadedAttachments = {
  attachments: [],
  isUploading: false
};

const scrappedAttachmentsInitialValue: IScrappedAttachments = {
  attachment: null,
  isScrapping: false
};

const UPLOADED_ATTACHMENT_TYPES = ["photo", "video", "pdf"];

const countUploadedAttachments = (channelAttachments: IChannelAttachments) => {
  switch (channelAttachments.type) {
    case "photo":
      return channelAttachments.photoAttachments?.attachments.length ?? 0;
    case "video":
      return channelAttachments.videoAttachments?.attachments.length ?? 0;
    case "pdf":
      return channelAttachments.pdfAttachments?.attachments.length ?? 0;
    default:
      return 0;
  }
};

// The toolbar has no button for "article", so a scraped link would stay unreachable
// for the rest of the session once an upload took its place. Only call this when an
// attachment was cleared, never when a type was chosen: the newly chosen type is
// still empty at that point and the article would steal it back.
const revealScrapedArticle = (
  channelAttachments: IChannelAttachments
): IChannelAttachments =>
  channelAttachments.articleAttachments?.attachment
    ? { ...channelAttachments, type: "article" }
    : channelAttachments;

const hasEmptiedUploadedAttachments = (
  channelAttachments: IChannelAttachments
) =>
  UPLOADED_ATTACHMENT_TYPES.includes(channelAttachments.type ?? "") &&
  countUploadedAttachments(channelAttachments) === 0;

export const initialValues: IComposerState = {
  isComposerOpen: false,
  inProMode: false,
  inDraftMode: false,
  postData: {},
  inSplitMode: false,
  tags: [],
  editorStateObj: {},
  visibleCaption: "all",
  validationErrors: [],
  chosenSuggestedCaption: {},
  postConcepts: null,
  postIdea: null,
  attachments: {
    all: {
      type: null,
      options: {},
      validationErrors: [],
      photoAttachments: { ...uploadedAttachmentsInitialValue },
      videoAttachments: { ...uploadedAttachmentsInitialValue },
      pdfAttachments: { ...uploadedAttachmentsInitialValue },
      articleAttachments: { ...scrappedAttachmentsInitialValue }
    }
  },
  scrapeResultsByUrl: {},
  scheduler: {
    isSubmitting: false,
    isDeleting: false,
    wasScheduleChanged: false,
    pickedDate: null,
    optionId: null
  },
  moodFilter: MOOD_FILTER_OPTIONS[0],
  suggestedImages: {
    isLoading: false,
    newImagesIndication: false,
    searchTerm: "",
    images: [],
    query: ""
  },
  activeToolkit: "captionSuggestion",
  agentChat: { history: [], previousLog: null, profileId: null, profileChosenBy: null, profiles: [], isReplying: false, chatId: null },
  agentTouch: { caption: null, visual: null, attachments: null, channels: null, schedule: null, category: null, profile: null },
  events: {
    onPostedEvent: createEvent("@@composerContext/onPostedEvent"),
    onReloadEvent: createEvent("@@composerContext/onReloadEvent"),
    onDeletedEvent: createEvent("@@composerContext/onDeletedEvent")
  },
  confirmationModalProps: null,
  isComposerCloseConfirmationOpen: false,
  closeToRefreshChannels: false,
  caption: "",
  composerNotifications: []
};

export const composerReducer = (
  state: IComposerState = initialValues,
  action: IActions
): IComposerState => {
  switch (action.type) {
    case OPEN_COMPOSER:
      return {
        ...state,
        isComposerOpen: true
      };

    case CLOSE_COMPOSER:
      return {
        ...state,
        isComposerOpen: false,
        scrapeResultsByUrl: {}
      };

    case SET_SCRAPE_RESULT:
      return {
        ...state,
        scrapeResultsByUrl: {
          ...state.scrapeResultsByUrl,
          [action.url]: action.payload
        }
      };

    case TOGGLE_PRO_MODE: {
      const enableProMode = action.payload ?? !state.inProMode;

      if (enableProMode) {
        store.dispatch(trackAnalyticsEvent("Pro Mode Enabled"));
      } else {
        store.dispatch(trackAnalyticsEvent("Pro Mode Disabled"));
      }

      storageService.set(
        KEY_COMPOSER_PRO_MODE,
        enableProMode ? "true" : "false"
      );

      return {
        ...state,
        inProMode: enableProMode
      };
    }

    case SET_IN_DRAFT_MODE:
      return {
        ...state,
        inDraftMode: action.payload
      };

    case SET_POST_DATA: {
      return {
        ...state,
        postData: {
          ...state.postData,
          ...action.payload
        }
      };
    }

    case RESET_POST_DATA: {
      return {
        ...initialValues
      };
    }

    case ENABLE_SPLIT_MODE:
      return {
        ...state,
        inSplitMode: true
      };

    case DISABLE_SPLIT_MODE:
      return {
        ...state,
        inSplitMode: false
      };

    case SET_TAGS: {
      return {
        ...state,
        tags: action.payload
      };
    }

    case SET_EDITOR_STATE_OBJ: {
      return {
        ...state,
        editorStateObj: action.payload
      };
    }

    case SET_VISIBLE_CAPTION: {
      return {
        ...state,
        visibleCaption: action.payload,
        // ? initiate attachment object for the channel
        // ? (only applicable when there is a need for different attachments for different channels)
        attachments: {
          ...state.attachments,
          [action.payload]: {
            ...(state.attachments[action.payload] ||
              initialValues.attachments["all"])
          }
        }
      };
    }

    case SET_VALIDATION_ERRORS: {
      return {
        ...state,
        validationErrors: {
          ...state.validationErrors,
          ...action.payload
        }
      };
    }

    case ADD_COMPOSER_NOTIFICATION: {
      return {
        ...state,
        composerNotifications: [...state.composerNotifications, action.payload]
      };
    }

    case REMOVE_COMPOSER_NOTIFICATION: {
      return {
        ...state,
        composerNotifications: state.composerNotifications.filter(
          n => n.id !== action.payload
        )
      };
    }

    case SET_CHOSEN_SUGGESTED_CAPTION: {
      return {
        ...state,
        chosenSuggestedCaption: {
          ...state.chosenSuggestedCaption,
          ...action.payload
        }
      };
    }

    case SET_POST_CONCEPTS: {
      return {
        ...state,
        postConcepts: action.payload
      };
    }

    case SET_POST_IDEA_CAPTIONS: {
      const postConcepts = state.postConcepts ?? [];

      const { postConceptIndex, postIdeaIndex } = postConcepts.reduce(
        (acc, postConcept, postConceptIndex) => {
          const postIdeaIndex = postConcept.postIdeas.findIndex(
            postIdea => postIdea.id === action.postIdeaId
          );

          if (postIdeaIndex > -1) {
            acc.postConceptIndex = postConceptIndex;
            acc.postIdeaIndex = postIdeaIndex;
          }
          return acc;
        },
        { postConceptIndex: -1, postIdeaIndex: -1 }
      );

      if (postConceptIndex === -1 || postIdeaIndex === -1) {
        return state;
      }

      const updatedPostConcept = {
        ...postConcepts[postConceptIndex],
        postIdeas: [
          ...postConcepts[postConceptIndex].postIdeas.slice(0, postIdeaIndex),
          {
            ...postConcepts[postConceptIndex].postIdeas[postIdeaIndex],
            captions: action.payload
          },
          ...postConcepts[postConceptIndex].postIdeas.slice(postIdeaIndex + 1)
        ]
      };

      return {
        ...state,
        postConcepts: [
          ...postConcepts.slice(0, postConceptIndex),
          updatedPostConcept,
          ...postConcepts.slice(postConceptIndex + 1)
        ]
      };
    }

    case SET_ATTACHMENT_TYPE: {
      const channelAttachments = {
        ...(state.attachments[action.channel] ||
          initialValues.attachments["all"]),
        type: action.payload
      };

      return {
        ...state,
        attachments: {
          ...state.attachments,
          [action.channel]:
            action.payload === null
              ? revealScrapedArticle(channelAttachments)
              : channelAttachments
        }
      };
    }

    case SET_IS_UPLOADING_ATTACHMENTS: {
      let newValue = {
        ...(state.attachments[action.channel] ||
          initialValues.attachments["all"]),
        type:
          action.attachmentType === "article"
            ? state.attachments[action.channel].type === null
              ? action.attachmentType
              : state.attachments[action.channel].type
            : action.attachmentType
      } as IChannelAttachments;
      if (action.attachmentType === "photo") {
        newValue = {
          ...newValue,
          photoAttachments: {
            ...(state.attachments[action.channel].photoAttachments ||
              uploadedAttachmentsInitialValue),
            isUploading: action.payload
          }
        };
      } else if (action.attachmentType === "video") {
        newValue = {
          ...newValue,
          videoAttachments: {
            ...(state.attachments[action.channel].videoAttachments ||
              uploadedAttachmentsInitialValue),
            isUploading: action.payload
          }
        };
      } else if (action.attachmentType === "pdf") {
        newValue = {
          ...newValue,
          pdfAttachments: {
            ...(state.attachments[action.channel].pdfAttachments ||
              uploadedAttachmentsInitialValue),
            isUploading: action.payload
          }
        };
      } else {
        newValue = {
          ...newValue,
          articleAttachments: {
            ...(state.attachments[action.channel].articleAttachments ||
              scrappedAttachmentsInitialValue),
            isScrapping: action.payload
          }
        };
      }

      return {
        ...state,
        attachments: {
          ...state.attachments,
          [action.channel]: newValue
        }
      };
    }

    case ADD_ATTACHMENTS: {
      let newValue = {
        ...(state.attachments[action.channel] ||
          initialValues.attachments["all"])
      } as IChannelAttachments;
      if (action.attachmentType === "photo") {
        newValue = {
          ...newValue,
          html: undefined,
          photoAttachments: {
            ...(state.attachments[action.channel].photoAttachments ||
              uploadedAttachmentsInitialValue),
            attachments: [
              ...newValue.photoAttachments.attachments,
              ...action.payload
            ]
          }
        };
      } else if (action.attachmentType === "video") {
        newValue = {
          ...newValue,
          videoAttachments: {
            ...(state.attachments[action.channel].videoAttachments ||
              uploadedAttachmentsInitialValue),
            attachments: [...action.payload] // ? replacing the video
          }
        };
      } else if (action.attachmentType === "pdf") {
        newValue = {
          ...newValue,
          html: undefined,
          pdfAttachments: {
            ...(state.attachments[action.channel].pdfAttachments ||
              uploadedAttachmentsInitialValue),
            attachments: [...action.payload] // ? replacing the pdf
          }
        };
      } else {
        newValue = {
          ...newValue,
          articleAttachments: {
            ...(state.attachments[action.channel].articleAttachments ||
              scrappedAttachmentsInitialValue),
            attachment: {
              ...action.payload
            }
          }
        };
      }

      return {
        ...state,
        attachments: {
          ...state.attachments,
          [action.channel]: newValue
        }
      };
    }

    case REMOVE_ATTACHMENT: {
      let newValue = {
        ...(state.attachments[action.channel] ||
          initialValues.attachments["all"])
      } as IChannelAttachments;
      if (action.attachmentType === "photo") {
        newValue = {
          ...newValue,
          html: undefined,
          photoAttachments: {
            ...(state.attachments[action.channel].photoAttachments ||
              uploadedAttachmentsInitialValue),
            attachments: [
              ...newValue.photoAttachments.attachments.slice(0, action.index),
              ...newValue.photoAttachments.attachments.slice(action.index + 1)
            ]
          }
        };
      } else if (action.attachmentType === "video") {
        newValue = {
          ...newValue,
          videoAttachments: {
            ...(state.attachments[action.channel].videoAttachments ||
              uploadedAttachmentsInitialValue),
            attachments: [
              ...newValue.videoAttachments.attachments.slice(0, action.index),
              ...newValue.videoAttachments.attachments.slice(action.index + 1)
            ]
          }
        };
      } else if (action.attachmentType === "pdf") {
        newValue = {
          ...newValue,
          html: undefined,
          pdfAttachments: {
            ...(state.attachments[action.channel].pdfAttachments ||
              uploadedAttachmentsInitialValue),
            attachments: [
              ...newValue.pdfAttachments.attachments.slice(0, action.index),
              ...newValue.pdfAttachments.attachments.slice(action.index + 1)
            ]
          }
        };
      } else if (action.attachmentType === "html") {
        newValue = {
          ...newValue,
          type: null,
          html: undefined,
          htmlMetaData: undefined,
          htmlClippedSlides: undefined,
          htmlEdited: undefined
        };
      }

      return {
        ...state,
        attachments: {
          ...state.attachments,
          [action.channel]: hasEmptiedUploadedAttachments(newValue)
            ? revealScrapedArticle(newValue)
            : newValue
        }
      };
    }

    case REMOVE_ARTICLE_ATTACHMENT: {
      const newValue = {
        ...(state.attachments[action.channel] ||
          initialValues.attachments["all"]),
        type: null,
        articleAttachments: {
          ...(state.attachments[action.channel].articleAttachments ||
            scrappedAttachmentsInitialValue),
          attachment: null
        }
      };

      return {
        ...state,
        attachments: {
          ...state.attachments,
          [action.channel]: newValue
        }
      };
    }

    case REORDER_ATTACHMENT: {
      let newValue = {
        ...(state.attachments[action.channel] ||
          initialValues.attachments["all"])
      } as IChannelAttachments;
      if (action.attachmentType === "photo") {
        const attachments = [...newValue.photoAttachments.attachments];
        const [selectedItem] = attachments.splice(action.sourceIndex, 1);
        attachments.splice(action.destinationIndex, 0, selectedItem);

        newValue = {
          ...newValue,
          photoAttachments: {
            ...(state.attachments[action.channel].photoAttachments ||
              uploadedAttachmentsInitialValue),
            attachments
          }
        };
      } else if (action.attachmentType === "video") {
        const attachments = [...newValue.videoAttachments.attachments];
        const [selectedItem] = attachments.splice(action.sourceIndex, 1);
        attachments.splice(action.destinationIndex, 0, selectedItem);

        newValue = {
          ...newValue,
          videoAttachments: {
            ...(state.attachments[action.channel].videoAttachments ||
              uploadedAttachmentsInitialValue),
            attachments
          }
        };
      } else if (action.attachmentType === "pdf") {
        const attachments = [...newValue.pdfAttachments.attachments];
        const [selectedItem] = attachments.splice(action.sourceIndex, 1);
        attachments.splice(action.destinationIndex, 0, selectedItem);

        newValue = {
          ...newValue,
          pdfAttachments: {
            ...(state.attachments[action.channel].pdfAttachments ||
              uploadedAttachmentsInitialValue),
            attachments
          }
        };
      }

      return {
        ...state,
        attachments: {
          ...state.attachments,
          [action.channel]: newValue
        }
      };
    }

    case SET_ATTACHMENT_VALIDATION_ERROR: {
      return {
        ...state,
        attachments: {
          ...state.attachments,
          [action.channel]: {
            ...(state.attachments[action.channel] ||
              initialValues.attachments["all"]),
            validationErrors: action.payload
          }
        }
      };
    }

    case SET_ATTACHMENTS: {
      return {
        ...state,
        attachments: {
          ...state.attachments,
          [action.channel]: {
            ...(state.attachments[action.channel] ||
              initialValues.attachments["all"]),
            ...action.payload
          }
        }
      };
    }

    case SET_ATTACHMENT_OPTIONS: {
      return {
        ...state,
        attachments: {
          ...state.attachments,
          [action.channel]: {
            ...(state.attachments[action.channel] ||
              initialValues.attachments["all"]),
            options: {
              ...(
                state.attachments[action.channel] ||
                initialValues.attachments["all"]
              ).options,
              ...action.payload
            }
          }
        }
      };
    }

    case SET_ATTACHMENT_HTML: {
      return {
        ...state,
        attachments: {
          ...state.attachments,
          [action.channel]: {
            ...(state.attachments[action.channel] || initialValues.attachments["all"]),
            html: action.payload,
            htmlEdited: true
          }
        }
      };
    }

    case SET_IS_SUBMITTING: {
      return {
        ...state,
        scheduler: {
          ...state.scheduler,
          isSubmitting: action.payload
        }
      };
    }

    case SET_IS_DELETING: {
      return {
        ...state,
        scheduler: {
          ...state.scheduler,
          isDeleting: action.payload
        }
      };
    }

    case SET_WAS_SCHEDULE_CHANGED: {
      return {
        ...state,
        scheduler: {
          ...state.scheduler,
          wasScheduleChanged: action.payload
        }
      };
    }

    case SET_PICKED_DATE: {
      return {
        ...state,
        scheduler: {
          ...state.scheduler,
          pickedDate: action.payload
        }
      };
    }

    case SET_MOOD_FILTER: {
      return {
        ...state,
        moodFilter: action.payload
      };
    }

    case SET_SCHEDULER_OPTION_ID: {
      return {
        ...state,
        scheduler: {
          ...state.scheduler,
          optionId: action.payload
        }
      };
    }

    case SET_AGENT_CHAT: {
      return {
        ...state,
        agentChat: { ...state.agentChat, ...action.payload }
      };
    }

    case MARK_AGENT_CHANGE: {
      return {
        ...state,
        agentTouch: { ...state.agentTouch, [action.payload.area]: action.payload.at }
      };
    }

    case SET_VISUAL_OVERFLOW: {
      return {
        ...state,
        attachments: {
          ...state.attachments,
          [action.channel]: {
            ...(state.attachments[action.channel] || initialValues.attachments["all"]),
            htmlClippedSlides: action.payload ?? undefined
          }
        }
      };
    }

    case SET_IS_LOADING_SUGGESTED_IMAGES: {
      return {
        ...state,
        suggestedImages: {
          ...state.suggestedImages,
          isLoading: action.payload
        }
      };
    }

    case SET_NEW_IMAGES_INDICATION: {
      return {
        ...state,
        suggestedImages: {
          ...state.suggestedImages,
          newImagesIndication: action.payload
        }
      };
    }

    case SET_IMAGES_SEARCH_TERM: {
      return {
        ...state,
        suggestedImages: {
          ...state.suggestedImages,
          searchTerm: action.payload
        }
      };
    }

    case SET_SUGGESTED_IMAGES: {
      return {
        ...state,
        suggestedImages: {
          ...state.suggestedImages,
          images:
            action.payload.length <= 0
              ? []
              : [...state.suggestedImages.images, ...action.payload]
        }
      };
    }

    case SET_IMAGES_QUERY: {
      return {
        ...state,
        suggestedImages: {
          ...state.suggestedImages,
          query: action.payload
        }
      };
    }

    case SET_ACTIVE_TOOLKIT: {
      return {
        ...state,
        activeToolkit: action.payload
      };
    }

    case SET_CONFIRMATION_MODAL_PROPS: {
      return {
        ...state,
        confirmationModalProps: action.payload
      };
    }

    case SET_IS_COMPOSER_CLOSE_CONFIRMATION_OPEN: {
      return {
        ...state,
        isComposerCloseConfirmationOpen:
          action.payload.isComposerCloseConfirmationOpen,
        closeToRefreshChannels:
          typeof action.payload.closeToRefreshChannels === "boolean"
            ? action.payload.closeToRefreshChannels
            : false
      };
    }

    case SET_CAPTION: {
      return {
        ...state,
        caption: action.payload
      };
    }

    default: {
      throw new Error("Unhandled action type");
    }
  }
};
